import config from "../components/common/Config";

const STORAGE_KEY = "active_tabs";
const LEGACY_STORAGE_KEY = "ebusso_active_tabs_v1";
const TAB_ID_KEY = "active_tab_id";
const LEGACY_TAB_ID_KEY = "ebusso_tab_session_id";
const UNLOAD_LOGOUT_NOTICE_KEY = "ebusso_unload_logout_notice_v1";
const UNLOAD_TOKEN_SESSION_KEY = "ebusso_unload_jwt";
const UNLOAD_TOUCH_KEY = "ebusso_unload_touch";
const REACT_WS_LOGOUT_READY_KEY = "ebusso_react_ws_logout_ready";

export const LEAVE_TAB_CONFIRM_MESSAGE =
  "Are you sure you want to leave this page?";

export function getWsLogoutUrlForApiBase(apiUrl) {
  if (!apiUrl) return "/ws-logout (relative to API base)";
  const base = String(apiUrl).replace(/\/+$/, "");
  return `${base}/ws-logout`;
}

function isLocalDevHostname(hostname) {
  const h = String(hostname || "").toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

export function getWsLogoutUnloadRequestUrl(apiUrl) {
  const direct = getWsLogoutUrlForApiBase(apiUrl);
  if (typeof window === "undefined" || !apiUrl) return direct;
  try {
    const base = String(apiUrl).replace(/\/+$/, "");
    const parsed = new URL(base);
    if (!isLocalDevHostname(window.location.hostname)) return direct;
    if (parsed.origin === window.location.origin) return direct;
    const segment =
      parsed.pathname === "/" ? "" : parsed.pathname.replace(/\/+$/, "");
    const mid = segment ? (segment.startsWith("/") ? segment : `/${segment}`) : "";
    return `${window.location.origin}/ebusso_api${mid}/ws-logout`;
  } catch {
    return direct;
  }
}
const PING_PREFIX = "active_tab_ping_";
const OTHER_TAB_ALIVE_MAX_MS = 120000;

function pingKey(id) {
  return `${PING_PREFIX}${String(id)}`;
}

function getAuthToken() {
  try {
    return localStorage.getItem("token") || localStorage.getItem("accessToken");
  } catch {
    return null;
  }
}

function getAuthTokenForUnload() {
  const t = getAuthToken();
  if (t) return t;
  try {
    return sessionStorage.getItem(UNLOAD_TOKEN_SESSION_KEY) || null;
  } catch {
    return null;
  }
}

function readTabs() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) {
      const leg = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (leg) {
        localStorage.setItem(STORAGE_KEY, leg);
        try {
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        } catch (_) {}
        raw = leg;
      }
    }
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeTabs(tabs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
}

function touchPing(tabId) {
  try {
    localStorage.setItem(pingKey(tabId), String(Date.now()));
  } catch (_) {}
}

function removePing(tabId) {
  try {
    localStorage.removeItem(pingKey(tabId));
  } catch (_) {}
}

function hasFreshPingMs(id, now, maxAgeMs) {
  const raw = localStorage.getItem(pingKey(id));
  if (!raw) return false;
  const t = parseInt(raw, 10);
  return Number.isFinite(t) && now - t < maxAgeMs;
}

function getOrCreateTabId() {
  let id =
    sessionStorage.getItem(TAB_ID_KEY) || sessionStorage.getItem(LEGACY_TAB_ID_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(TAB_ID_KEY, id);
  } else if (!sessionStorage.getItem(TAB_ID_KEY)) {
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
}

function clearTabIdFromSession() {
  try {
    sessionStorage.removeItem(TAB_ID_KEY);
    sessionStorage.removeItem(LEGACY_TAB_ID_KEY);
  } catch (_) {}
}

export function registerActiveTab() {
  if (!getAuthToken()) return;
  try {
    const tok = localStorage.getItem("token") || localStorage.getItem("accessToken");
    if (tok) {
      sessionStorage.setItem(UNLOAD_TOKEN_SESSION_KEY, tok);
    }
  } catch (_) {}
  const tabId = getOrCreateTabId();
  const tabs = readTabs();
  if (!tabs.includes(tabId)) {
    tabs.push(tabId);
    writeTabs(tabs);
  }
  touchPing(tabId);
}

export function removeCurrentTabFromRegistry() {
  const tabId =
    sessionStorage.getItem(TAB_ID_KEY) || sessionStorage.getItem(LEGACY_TAB_ID_KEY);
  if (!tabId) return;
  const next = readTabs().filter((id) => String(id) !== String(tabId));
  writeTabs(next);
  removePing(tabId);
  clearTabIdFromSession();
}

function requestWsLogoutAtUnload(apiUrl) {
  const token = getAuthTokenForUnload();
  if (!token || !apiUrl) return;
  const bearer =
    (typeof localStorage !== "undefined" && localStorage.getItem("token")) || token;
  const url = getWsLogoutUnloadRequestUrl(apiUrl);
  const usp = new URLSearchParams();
  usp.set("Platform", "Web");
  usp.set("token", token);
  const encoded = usp.toString();
  const authValue = String(bearer).replace(/^Bearer\s+/i, "").trim();
  try {
    void fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authValue}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: encoded,
      keepalive: true,
      credentials: "include",
      mode: "cors",
    });
  } catch (_) {}
}

export function removeThisTabFromListOnly() {
  const tabId =
    sessionStorage.getItem(TAB_ID_KEY) || sessionStorage.getItem(LEGACY_TAB_ID_KEY);
  if (!tabId) return;
  const next = readTabs().filter((id) => String(id) !== String(tabId));
  writeTabs(next);
}

function unregisterForUnload(apiUrl) {
  const token = getAuthTokenForUnload();
  if (!token) return;
  const resolvedApi = apiUrl || resolveUnloadApiUrl();
  if (!resolvedApi) {
    try {
      localStorage.setItem(
        UNLOAD_LOGOUT_NOTICE_KEY,
        JSON.stringify({ t: Date.now(), reason: "no_api_url" })
      );
    } catch (_) {}
    return;
  }
  registerActiveTab();
  const tabId = getOrCreateTabId();
  const remaining = readTabs().filter((id) => String(id) !== String(tabId));
  const now = Date.now();
  const aliveOthers = remaining.filter((id) =>
    hasFreshPingMs(id, now, OTHER_TAB_ALIVE_MAX_MS)
  );
  writeTabs(aliveOthers);
  const lastTabForNotice = aliveOthers.length === 0;
  if (lastTabForNotice) {
    try {
      localStorage.setItem(
        UNLOAD_LOGOUT_NOTICE_KEY,
        JSON.stringify({ t: Date.now() })
      );
    } catch (_) {}
    writeTabs([]);
  }
}

let unloadListenerInstalled = false;
let unloadGetApiUrl = null;
let unloadHandlerRan = false;

function resolveUnloadApiUrl() {
  const fromGetter =
    typeof unloadGetApiUrl === "function" ? unloadGetApiUrl() : unloadGetApiUrl;
  if (fromGetter) return fromGetter;
  try {
    if (config && config.API_URL) return config.API_URL;
  } catch (_) {}
  try {
    if (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
  } catch (_) {}
  return "";
}

function onActualPageUnload(event) {
  if (event && event.type === "pagehide" && event.persisted) return;
  try {
    localStorage.setItem(
      UNLOAD_TOUCH_KEY,
      JSON.stringify({ t: Date.now(), et: event && event.type })
    );
  } catch (_) {}
  if (unloadHandlerRan) return;
  unloadHandlerRan = true;
  const apiUrl = resolveUnloadApiUrl();
  if (getAuthTokenForUnload() && apiUrl) {
    requestWsLogoutAtUnload(apiUrl);
  }
  unregisterForUnload(apiUrl);
}

export function ensureUnloadLastTabListener(getApiUrl) {
  unloadGetApiUrl = getApiUrl;
  try {
    const base = typeof getApiUrl === "function" ? getApiUrl() : getApiUrl;
    if (base) {
      localStorage.setItem("ebusso_ws_logout_full_url", getWsLogoutUnloadRequestUrl(base));
    }
  } catch (_) {}
  if (unloadListenerInstalled) return;
  unloadListenerInstalled = true;
  window.addEventListener("pagehide", onActualPageUnload, true);
  try {
    sessionStorage.setItem(REACT_WS_LOGOUT_READY_KEY, "1");
  } catch (_) {}
}

export function primeTabSessionIfLoggedIn() {
  if (!getAuthToken()) return;
  registerActiveTab();
}

export function teardownActiveTabSession() {
  return () => {
    removeThisTabFromListOnly();
  };
}
